import * as THREE from 'three'

export class LanternInteractionManager {
  constructor(camera, canvas, lanternMeshes, onHover, physics) {
    this.camera = camera
    this.canvas = canvas
    this.lanternMeshes = lanternMeshes
    this.onHover = onHover
    this.physics = physics
    this.raycaster = new THREE.Raycaster()
    this.mouse = new THREE.Vector2()
    this.lastHitIndex = -1
    this.enabled = true

    // Swipe state
    this.swipeActive = false
    this.lastTouchX = 0
    this.lastTouchY = 0
    this.lastTouchTime = 0
    this.lanternCooldowns = new Map() // index -> last impulse time

    this._onMouseMove = this._onMouseMove.bind(this)
    this._onTouchStart = this._onTouchStart.bind(this)
    this._onTouchMove = this._onTouchMove.bind(this)
    this._onTouchEnd = this._onTouchEnd.bind(this)

    window.addEventListener('mousemove', this._onMouseMove)
    this.canvas.addEventListener('touchstart', this._onTouchStart, { passive: true })
    this.canvas.addEventListener('touchmove', this._onTouchMove, { passive: true })
    this.canvas.addEventListener('touchend', this._onTouchEnd, { passive: true })
    this.canvas.addEventListener('touchcancel', this._onTouchEnd, { passive: true })
  }

  setEnabled(enabled) {
    this.enabled = enabled
    this.swipeActive = false
    this.lanternCooldowns.clear()
  }

  _getTouchPos(touch) {
    return {
      x: (touch.clientX / window.innerWidth) * 2 - 1,
      y: -(touch.clientY / window.innerHeight) * 2 + 1
    }
  }

  _raycastFromPointer(x, y) {
    this.mouse.x = x
    this.mouse.y = y
    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.lanternMeshes, true)

    if (intersects.length > 0) {
      let obj = intersects[0].object
      while (obj) {
        const index = this.lanternMeshes.indexOf(obj)
        if (index !== -1) return index
        obj = obj.parent
      }
    }
    return -1
  }

  _onMouseMove(event) {
    if (!this.enabled || !this.camera) return

    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1

    this.raycaster.setFromCamera(this.mouse, this.camera)
    const intersects = this.raycaster.intersectObjects(this.lanternMeshes, true)

    if (intersects.length > 0) {
      const hit = intersects[0]
      let obj = hit.object
      let index = -1
      while (obj) {
        index = this.lanternMeshes.indexOf(obj)
        if (index !== -1) break
        obj = obj.parent
      }
      if (index !== -1 && index !== this.lastHitIndex) {
        this.onHover(index)
        this.lastHitIndex = index
      }
    } else {
      this.lastHitIndex = -1
    }
  }

  _onTouchStart(event) {
    if (!this.enabled || !this.camera) return

    const touch = event.touches[0]
    this.swipeActive = true
    this.lastTouchX = touch.clientX
    this.lastTouchY = touch.clientY
    this.lastTouchTime = performance.now()

    // Give a small tap impulse if starting directly on a lantern
    const pos = this._getTouchPos(touch)
    const hitIndex = this._raycastFromPointer(pos.x, pos.y)
    if (hitIndex !== -1) {
      this.physics.applyImpulse(hitIndex, 0.06)
      this.lanternCooldowns.set(hitIndex, performance.now())
    }
  }

  _onTouchMove(event) {
    if (!this.swipeActive || !this.camera) return

    const touch = event.touches[0]
    const now = performance.now()
    const dt = now - this.lastTouchTime

    if (dt > 0) {
      const dx = touch.clientX - this.lastTouchX
      const dy = touch.clientY - this.lastTouchY
      const distance = Math.sqrt(dx * dx + dy * dy)
      const speed = distance / dt // px per ms

      const pos = this._getTouchPos(touch)
      const hitIndex = this._raycastFromPointer(pos.x, pos.y)

      if (hitIndex !== -1) {
        const lastHit = this.lanternCooldowns.get(hitIndex) || 0
        if (now - lastHit > 80) {
          const force = Math.min(speed * 0.4, 0.2)
          if (force > 0.02) {
            this.physics.applyImpulse(hitIndex, force)
            this.lanternCooldowns.set(hitIndex, now)
          }
        }
      }
    }

    this.lastTouchX = touch.clientX
    this.lastTouchY = touch.clientY
    this.lastTouchTime = now
  }

  _onTouchEnd(event) {
    this.swipeActive = false
    this.lanternCooldowns.clear()
  }

  destroy() {
    window.removeEventListener('mousemove', this._onMouseMove)
    this.canvas.removeEventListener('touchstart', this._onTouchStart)
    this.canvas.removeEventListener('touchmove', this._onTouchMove)
    this.canvas.removeEventListener('touchend', this._onTouchEnd)
    this.canvas.removeEventListener('touchcancel', this._onTouchEnd)
    this.camera = null
    this.canvas = null
    this.lanternMeshes = []
    this.onHover = null
    this.physics = null
  }
}
